import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, Typography, Button, Drawer } from "@mui/material";
import { FilterList } from '@mui/icons-material';
import { EFilterType, IFilter } from "../../../../store/reducers/scheduledServices";
import * as actions from '../../../../store/actions';
import { RootState } from "../../../../store/types";
import { i18n } from "../../../../translate/i18n";
import { ISite } from "../../../../models/isite";
import ScheduledServicesCalendarsFiltersModal from "../Modals/ScheduledServicesCalendarFiltersModal";
import { LoadingButton } from "@mui/lab";
import { Launch } from '@mui/icons-material';
import { formatLanguageFromLocalStorage } from "../../../../utils/formatLanguageFromLocalStorage";

type Props = {
  calendarDate: Date,
}

const ScheduledServicesCalendarHeader: React.FC<Props> = ({ calendarDate }) => {
  const dispatch = useDispatch();
  const [filtersModalOpen, setFiltersModalOpen] = useState<boolean>(false);

  const filteredSites = useSelector((state: any) => state.sites.selectedSitesPlaces);
  const filters = useSelector((state: RootState) => state.scheduledServices.calendar.filters);
  const { areas, companies, equipmentGroups, equipmentModels, frequencies, places, serviceTypes, users } = filters;

  const loading = useSelector((state: RootState) => state.scheduledServices.loadingCalendarReportExcell);

  const localStorageLang = localStorage.getItem("i18nextLng");
  const language = formatLanguageFromLocalStorage(localStorageLang);

  const fetchCalendarReportExcell = useCallback(async () => dispatch(actions.scheduledServicesFetchCalendarReportExcell(calendarDate.toISOString(), filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies, language!)) as any, [calendarDate, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies, language]);

  const deleteFilteredSite = (site: any) => {
    dispatch(actions.deleteFilteredSite(site));
  };

  const clearSelectedFilter = (filterId: string, type: EFilterType) => {
    dispatch(actions.scheduledServicesClearCalendarSelectedFilter(filterId, type));
  }

  const handleClickClearFilters = () => dispatch(actions.scheduledServicesClearCalendarFilters());

  const exportExcel = () => {
    fetchCalendarReportExcell()
      .then(response => {
        if (response) {
          window.open(response, "_blank")
        }
      })
      .catch((e) => console.log(e))
  }

  const renderSiteChips = () => (filteredSites && filteredSites.map((site: ISite) => 
    (<Chip
      key={site._id}
      label={site.name}
      onDelete={() => deleteFilteredSite(site)}
      style={styles.filterChip}
    />)
  ));

  const renderChips = (filterArray: IFilter[], type: EFilterType) => {
    return filterArray.map(filter => (
      <Chip
        key={filter.value}
        label={filter.label}
        onDelete={() => clearSelectedFilter(filter.value, type)}
        style={styles.filterChip}
      />
    ))
  };

  const clearFilterButton = () => {
    const isThereAnyFilter = (areas.length > 0)
      || (companies.length > 0)
      || (equipmentGroups.length > 0)
      || (equipmentModels.length > 0)
      || (frequencies.length > 0)
      || (places.length > 0)
      || (serviceTypes.length > 0)
      || (users.length > 0);
     return isThereAnyFilter && 
      <Typography onClick={handleClickClearFilters} style={styles.clearFilter}>{i18n.t('scheduledServicesCalendarHeader.clear') as string}</Typography>;
  };

  return(
    <Box>
      <Typography sx={styles.title} variant='h5'>{i18n.t('scheduledServicesCalendarHeader.title') as string}</Typography>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Button
          variant="contained"
          onClick={() => setFiltersModalOpen(true)}
          style={styles.buttonFilter}
        >
          {i18n.t('scheduledServicesCalendarHeader.filters') as string }
          <FilterList style={styles.buttonFilterIcon} />
        </Button>
        <LoadingButton
          variant='outlined'
          aria-label="add"
          style={{ width: 'fit-content', marginBottom: '10px' }}
          onClick={exportExcel} 
          disabled={loading}
          loading={loading}
          startIcon={<Launch />}
        >
          {i18n.t('btnTableTileMatOverrides.export') as string}
        </LoadingButton>
      </Box>
      <Box sx={styles.chipsContainer}>
        { renderSiteChips() }
        { renderChips(areas, EFilterType.areas) }
        { renderChips(companies, EFilterType.companies) }
        { renderChips(equipmentGroups, EFilterType.equipmentGroups) }
        { renderChips(equipmentModels, EFilterType.equipmentModels) }
        { renderChips(frequencies, EFilterType.frequencies) }
        { renderChips(serviceTypes, EFilterType.serviceTypes) }
        { renderChips(places, EFilterType.places) }
        { renderChips(users, EFilterType.users) }
        { clearFilterButton() }
      </Box>
      <Drawer
        variant="temporary"
        anchor="right"
        onClose={() => setFiltersModalOpen(false)}
        open={filtersModalOpen}>
          <ScheduledServicesCalendarsFiltersModal closeModal={() => setFiltersModalOpen(false)} />
      </Drawer>
    </Box>
  );
};

export default ScheduledServicesCalendarHeader;

const styles = {
  filterChip: {
    cursor: 'default',
    margin: '5px 10px 5px 0px',
  },
  clearFilter: {
    cursor: 'pointer',
    padding: '0.75rem 0.5rem',
    // color: '#4CAF50',
    color: 'black',
    textDecoration: 'underline',
  },
  buttonFilter: {
    backgroundColor: 'green',
    marginBottom: '10px',
    height: '2.2rem',
    width: '6.5rem',
    padding: '1rem',
    color: '#fff',
  },
  buttonFilterIcon: {
    marginLeft: '5px'
  },
  title: {
    marginTop: '10px',
    marginBottom: '50px',
  },
  chipsContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: "wrap",
  }
}